// Store: bridge between Y.Doc and UI
// Phase 0/1: placeholder — will be fully implemented in Phase 2

import * as Y from 'yjs';
import { createDoc, initDefaults, getNodesMap, getRootOrder, getTrash, getMeta, createNode, getNodeSnapshot, getAllNodes, deleteNode } from './yjs/doc.js';
import { uid, ts } from './utils.js';

let doc = null;
let renderCallback = null;

export function initStore() {
  doc = createDoc();
  initDefaults(doc);
  return doc;
}

export function getDoc() {
  return doc;
}

export function onStoreChange(callback) {
  renderCallback = callback;

  // Observe changes on all top-level maps/arrays
  getNodesMap(doc).observeDeep(() => renderCallback?.());
  getRootOrder(doc).observe(() => renderCallback?.());
  getTrash(doc).observe(() => renderCallback?.());
}

// ── Read helpers ───

export function getNodes() {
  return getAllNodes(doc);
}

export function getNode(id) {
  return getNodeSnapshot(doc, id);
}

export function getNodeMap(id) {
  return getNodesMap(doc).get(id);
}

export function getRootOrderArray() {
  return getRootOrder(doc).toArray();
}

// ── Responsive Profiles ─────────────────────────────────────────────
//
// 設定（フォントサイズ等）はウィンドウ幅で自動切替する3プロファイル(S/M/L)に保存される。
// 各プロファイルは独立した設定値を持ち、現在のウィンドウ幅が属するゾーンの
// プロファイルが自動的にアクティブになる。閾値は memo_profile_breakpoints に保存。
//
// 例: breakpoints=[600, 1100] のとき:
//   width <  600 → S プロファイル適用
//   600 ≤ width <1100 → M プロファイル適用
//   width ≥ 1100 → L プロファイル適用
//
// ユーザーが「プロファイル手動上書き」を選んだ場合は sessionStorage に
// 上書き先（S/M/L）を保存し、その間は自動切替が無効になる（タブごと）。

const PROFILES_KEY = 'memo_profiles';
const BREAKPOINTS_KEY = 'memo_profile_breakpoints';
const PROFILE_OVERRIDE_KEY = 'memo_profile_override';  // sessionStorage (per-tab)
const PROFILE_KEYS = ['S', 'M', 'L'];

const DEFAULT_BREAKPOINTS = [600, 1100];

function getBreakpoints() {
  try {
    const raw = localStorage.getItem(BREAKPOINTS_KEY);
    if (!raw) return [...DEFAULT_BREAKPOINTS];
    const arr = JSON.parse(raw);
    if (Array.isArray(arr) && arr.length === 2) return arr;
  } catch {}
  return [...DEFAULT_BREAKPOINTS];
}

export function setBreakpoints(b1, b2) {
  const lo = Math.min(b1, b2);
  const hi = Math.max(b1, b2);
  localStorage.setItem(BREAKPOINTS_KEY, JSON.stringify([lo, hi]));
}

export function getProfileBreakpoints() { return getBreakpoints(); }

// Determine which profile is active based on window width OR manual override.
export function getActiveProfile() {
  const override = sessionStorage.getItem(PROFILE_OVERRIDE_KEY);
  if (override && PROFILE_KEYS.includes(override)) return override;
  const w = window.innerWidth || 1024;
  const [b1, b2] = getBreakpoints();
  if (w < b1) return 'S';
  if (w < b2) return 'M';
  return 'L';
}

// Width-based active profile (ignoring override). Useful for the "auto" indicator.
export function getAutoProfile() {
  const w = window.innerWidth || 1024;
  const [b1, b2] = getBreakpoints();
  if (w < b1) return 'S';
  if (w < b2) return 'M';
  return 'L';
}

export function setProfileOverride(key) {
  if (key === null || key === 'auto') {
    sessionStorage.removeItem(PROFILE_OVERRIDE_KEY);
  } else if (PROFILE_KEYS.includes(key)) {
    sessionStorage.setItem(PROFILE_OVERRIDE_KEY, key);
  }
}
export function getProfileOverride() {
  return sessionStorage.getItem(PROFILE_OVERRIDE_KEY) || null;
}

function readAllProfiles() {
  try {
    const raw = localStorage.getItem(PROFILES_KEY);
    if (raw) {
      const obj = JSON.parse(raw);
      if (obj && typeof obj === 'object') return obj;
    }
  } catch {}
  return { S: {}, M: {}, L: {} };
}
function writeAllProfiles(obj) {
  localStorage.setItem(PROFILES_KEY, JSON.stringify(obj));
}

// Migrate legacy per-tab sessionStorage `memo_setting_*` values into ALL three
// profiles (so the user's current settings carry over and they can customize
// each profile from that starting point). Runs once on boot.
export function migrateLegacySettingsIfNeeded() {
  if (localStorage.getItem(PROFILES_KEY)) return; // already migrated
  const legacy = {};
  for (let i = 0; i < sessionStorage.length; i++) {
    const k = sessionStorage.key(i);
    if (k && k.startsWith('memo_setting_')) {
      const v = sessionStorage.getItem(k);
      try { legacy[k.replace('memo_setting_', '')] = JSON.parse(v); } catch { legacy[k.replace('memo_setting_', '')] = v; }
    }
  }
  const profiles = { S: { ...legacy }, M: { ...legacy }, L: { ...legacy } };
  writeAllProfiles(profiles);
}

export function getSetting(key) {
  const active = getActiveProfile();
  const profiles = readAllProfiles();
  const v = profiles[active]?.[key];
  return v === undefined ? null : v;
}

// Read setting from a specific profile (for the settings UI when editing other profiles).
export function getSettingFromProfile(profileKey, key) {
  const profiles = readAllProfiles();
  const v = profiles[profileKey]?.[key];
  return v === undefined ? null : v;
}

// Write a setting to a specific profile (default: active profile).
export function setSettingInProfile(profileKey, key, value) {
  const profiles = readAllProfiles();
  if (!profiles[profileKey]) profiles[profileKey] = {};
  profiles[profileKey][key] = value;
  writeAllProfiles(profiles);
}

export function getSelectedId() {
  return sessionStorage.getItem('memo_selectedId') || null;
}

export function getExpanded() {
  try {
    return new Set(JSON.parse(sessionStorage.getItem('memo_expanded') || '[]'));
  } catch {
    return new Set();
  }
}

export function getTrashEntries() {
  const trashMap = getTrash(doc);
  const result = {};
  trashMap.forEach((val, key) => {
    try { result[key] = JSON.parse(val); } catch {}
  });
  return result;
}

// ── Write helpers ───

export function addNode(type, parentId, name, afterId) {
  const id = uid();

  doc.transact(() => {
    createNode(doc, { id, type, name: name || (type === 'folder' ? '新しいフォルダ' : ''), parentId });

    // Add to parent's childOrder or rootOrder
    if (parentId) {
      const parentNode = getNodesMap(doc).get(parentId);
      if (parentNode) {
        const childOrder = parentNode.get('childOrder');
        if (childOrder) {
          if (afterId) {
            const idx = findInYArray(childOrder, afterId);
            if (idx >= 0) {
              childOrder.insert(idx + 1, [id]);
            } else {
              childOrder.push([id]);
            }
          } else {
            childOrder.push([id]);
          }
        }
      }
    } else {
      const rootOrder = getRootOrder(doc);
      if (afterId) {
        const idx = findInYArray(rootOrder, afterId);
        if (idx >= 0) {
          rootOrder.insert(idx + 1, [id]);
        } else {
          rootOrder.push([id]);
        }
      } else {
        rootOrder.push([id]);
      }
    }
  });

  return id;
}

export function renameNode(id, name) {
  const nodeMap = getNodesMap(doc).get(id);
  if (!nodeMap) return;
  doc.transact(() => {
    nodeMap.set('name', name);
    nodeMap.set('updatedAt', ts());
  });
}

export function updateContent(id, content) {
  const nodeMap = getNodesMap(doc).get(id);
  if (!nodeMap) return;

  doc.transact(() => {
    const text = nodeMap.get('content');
    if (text instanceof Y.Text) {
      text.delete(0, text.length);
      if (content) text.insert(0, content);
    }
    const firstLine = (content || '').split('\n')[0].trim();
    nodeMap.set('name', firstLine);
    nodeMap.set('updatedAt', ts());
  });
}

export function setSelectedId(id) {
  if (id) {
    sessionStorage.setItem('memo_selectedId', id);
  } else {
    sessionStorage.removeItem('memo_selectedId');
  }
}

export function setExpanded(expandedSet) {
  sessionStorage.setItem('memo_expanded', JSON.stringify([...expandedSet]));
}

export function setSetting(key, value) {
  const active = getActiveProfile();
  setSettingInProfile(active, key, value);
}

export function moveNode(id, newParentId, beforeId) {
  const nodesMap = getNodesMap(doc);
  const nodeMap = nodesMap.get(id);
  if (!nodeMap) return;

  const oldParentId = nodeMap.get('parentId');

  doc.transact(() => {
    // Remove from old parent
    if (oldParentId) {
      const oldParent = nodesMap.get(oldParentId);
      if (oldParent) {
        const oldOrder = oldParent.get('childOrder');
        const idx = findInYArray(oldOrder, id);
        if (idx >= 0) oldOrder.delete(idx, 1);
      }
    } else {
      const rootOrder = getRootOrder(doc);
      const idx = findInYArray(rootOrder, id);
      if (idx >= 0) rootOrder.delete(idx, 1);
    }

    // Set new parent
    nodeMap.set('parentId', newParentId || null);

    // Add to new parent
    if (newParentId) {
      const newParent = nodesMap.get(newParentId);
      if (newParent) {
        const newOrder = newParent.get('childOrder');
        if (beforeId) {
          const beforeIdx = findInYArray(newOrder, beforeId);
          if (beforeIdx >= 0) {
            newOrder.insert(beforeIdx, [id]);
          } else {
            newOrder.push([id]);
          }
        } else {
          newOrder.push([id]);
        }
      }
    } else {
      const rootOrder = getRootOrder(doc);
      if (beforeId) {
        const beforeIdx = findInYArray(rootOrder, beforeId);
        if (beforeIdx >= 0) {
          rootOrder.insert(beforeIdx, [id]);
        } else {
          rootOrder.push([id]);
        }
      } else {
        rootOrder.push([id]);
      }
    }
  });
}

export function sendToTrash(id) {
  const node = getNodeSnapshot(doc, id);
  if (!node) return;

  const subtree = collectSubtree(id);

  doc.transact(() => {
    // Remove from parent order
    const nodesMap = getNodesMap(doc);
    if (node.parentId) {
      const parent = nodesMap.get(node.parentId);
      if (parent) {
        const order = parent.get('childOrder');
        const idx = findInYArray(order, id);
        if (idx >= 0) order.delete(idx, 1);
      }
    } else {
      const rootOrder = getRootOrder(doc);
      const idx = findInYArray(rootOrder, id);
      if (idx >= 0) rootOrder.delete(idx, 1);
    }

    // Delete all nodes in subtree from Y.Doc
    Object.keys(subtree).forEach((sid) => deleteNode(doc, sid));

    // Add to trash
    getTrash(doc).set(id, JSON.stringify({
      displayName: node.name || '',
      type: node.type,
      originalParentId: node.parentId,
      deletedAt: ts(),
      subtree,
    }));
  });
}

export function restoreFromTrash(entryId) {
  const trashMap = getTrash(doc);
  const entryStr = trashMap.get(entryId);
  if (!entryStr) return;
  const entry = JSON.parse(entryStr);

  doc.transact(() => {
    // Recreate all nodes
    Object.values(entry.subtree).forEach((n) => {
      createNode(doc, { id: n.id, type: n.type, name: n.name, parentId: n.parentId, content: n.content });
      // Restore childOrder for folders
      if (n.type === 'folder' && n.childOrder) {
        const nodeMap = getNodesMap(doc).get(n.id);
        const childOrder = nodeMap.get('childOrder');
        n.childOrder.forEach((cid) => childOrder.push([cid]));
      }
    });

    // Add root node to parent or rootOrder
    const nodesMap = getNodesMap(doc);
    if (entry.originalParentId && nodesMap.has(entry.originalParentId)) {
      const parent = nodesMap.get(entry.originalParentId);
      const order = parent.get('childOrder');
      if (findInYArray(order, entryId) < 0) order.push([entryId]);
    } else {
      // Set parentId to null if original parent is gone
      const nodeMap = nodesMap.get(entryId);
      if (nodeMap) nodeMap.set('parentId', null);
      const rootOrder = getRootOrder(doc);
      if (findInYArray(rootOrder, entryId) < 0) rootOrder.push([entryId]);
    }

    // Remove from trash
    trashMap.delete(entryId);
  });
}

export function permanentDeleteTrash(entryId) {
  getTrash(doc).delete(entryId);
}

export function emptyTrash() {
  const trashMap = getTrash(doc);
  doc.transact(() => {
    const keys = [];
    trashMap.forEach((_, key) => keys.push(key));
    keys.forEach((key) => trashMap.delete(key));
  });
}

// ── Internal helpers ───

function findInYArray(yArray, value) {
  for (let i = 0; i < yArray.length; i++) {
    if (yArray.get(i) === value) return i;
  }
  return -1;
}

function collectSubtree(id) {
  const result = {};
  const node = getNodeSnapshot(doc, id);
  if (!node) return result;
  result[id] = node;
  if (node.type === 'folder') {
    (node.childOrder || []).forEach((cid) => Object.assign(result, collectSubtree(cid)));
  }
  return result;
}
